/**
 * WaterfallChartControl
 *
 * Waterfall chart for showing cumulative effect
 * Displays increases, decreases, and totals
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import { BaseChartComponent, type ChartDataPoint, extractChartData } from './BaseChartComponent';

const WaterfallChartControl: React.FC<ControlRendererProps> = memo(({ control, value }) => {
  // CRITICAL: ONLY use control.config.staticData
  const staticData = (control.config?.staticData as ChartDataPoint[] | undefined) || [];
  const chartData = useMemo(() => {
    return staticData; // ONLY use configured data
  }, [staticData]);

  const waterfallConfig = useMemo(() => {
    const config = control.config as Record<string, unknown> | undefined;
    return {
      labelKey: (config?.labelKey as string) || 'label',
      valueKey: (config?.valueKey as string) || 'value',
      showConnector: config?.showConnector !== false,
    };
  }, [control.config]);

  const renderWaterfall = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    // Extract values and calculate running total
    const waterData: Array<{ label: string; value: number; start: number; type: string }> = [];
    let runningTotal = 0;

    chartData.forEach((d) => {
      const label = String((d as Record<string, unknown>)[waterfallConfig.labelKey] || '');
      const value = (d as Record<string, unknown>)[waterfallConfig.valueKey] as number | undefined;

      if (typeof value === 'number') {
        const start = runningTotal;
        const isPositive = value >= 0;
        runningTotal += value;

        waterData.push({
          label,
          value: Math.abs(value),
          start: isPositive ? start : runningTotal,
          type: isPositive ? 'increase' : 'decrease',
        });
      }
    });

    // Add total
    waterData.push({
      label: 'Total',
      value: Math.abs(runningTotal),
      start: 0,
      type: 'total',
    });

    const padding = 40;
    const width = 600;
    const height = (control.config?.height as number) || 400;
    const plotWidth = width - 2 * padding;
    const plotHeight = height - 2 * padding;

    const maxValue = Math.max(...waterData.map((d) => d.start + d.value), 1);
    const yScale = plotHeight / maxValue;
    const barWidth = plotWidth / Math.max(waterData.length, 1);

    const colors = { increase: '#82ca9d', decrease: '#ff7c7c', total: '#8884d8' };

    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
        {/* Axes */}
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#000" />
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#000" />

        {/* Bars and connectors */}
        {waterData.map((item, index) => {
          const x = padding + index * barWidth;
          const barHeight = item.value * yScale;
          const y = height - padding - (item.start + item.value) * yScale;

          return (
            <g key={`waterfall-${index}`}>
              {/* Bar */}
              <rect
                x={x + 5}
                y={y}
                width={barWidth - 10}
                height={barHeight}
                fill={(colors[item.type as keyof typeof colors] || '#8884d8')}
                opacity="0.8"
              />

              {/* Label */}
              <text
                x={x + barWidth / 2}
                y={height - padding + 20}
                textAnchor="middle"
                fontSize="12"
                fill="#6b7280"
              >
                {item.label.substring(0, 8)}
              </text>

              {/* Connector to next bar */}
              {waterfallConfig.showConnector && index < waterData.length - 1 && (
                <line
                  x1={x + barWidth}
                  y1={height - padding - (item.start + item.value) * yScale}
                  x2={x + barWidth}
                  y2={height - padding - (item.start + item.value) * yScale}
                  stroke="#9CA3AF"
                  strokeDasharray="4,4"
                  strokeWidth="1"
                />
              )}
            </g>
          );
        })}
      </svg>
    );
  }, [chartData, waterfallConfig, control.config]);

  return (
    <BaseChartComponent control={control} value={value} chartType="waterfall-chart">
      <div style={{ width: '100%', height: '100%' }}>{renderWaterfall}</div>
    </BaseChartComponent>
  );
});

WaterfallChartControl.displayName = 'WaterfallChartControl';
export default WaterfallChartControl;
